import { html, repeat, ref, when } from '@microsoft/fast-element';
import { sync } from '@genesislcap/foundation-utils';
import { COMPANY_ROLE, EditCompany } from './edit';
import { completedTab } from '../../../../styles';
import { eventDetail } from '../../../../utils/formatting';
import { CREDIT_PROFILE } from '../../../../utils/enums/generic.enum';

export const editTemplate = html<EditCompany>`
  <div class="company-details">
    Manage ${(x) => (x.companyNameValue ? x.companyNameValue : 'Company')}
  </div>
  <zero-tabs ${ref('companyTabs')} orientation="vertical" activeId="company-detail">
    <zero-tab id="company-detail">
      <span
        style="display: ${(x) => (!x.isFormValid ? 'inline' : 'none')}"
        class="completed-tab-icon"
      >
        ${completedTab}
      </span>
      Company Details
    </zero-tab>
    ${when(
      (x) => (x.isEdit && x.isAdmin),
      html`
        <zero-tab id="user-detail">
          <span
            style="display: ${(x) => (x.companyHasUser ? 'inline' : 'none')}"
            class="completed-tab-icon"
          >
            ${completedTab}
          </span>
          Users
        </zero-tab>
      `,
    )}

    <zero-tab-panel>
      <div class="form-header">Company Details</div>
      <div class="form">
        <div class="form-column">
          <zero-text-field
            :value=${sync((x) => x.companyNameValue)}
            placeholder="Enter..."
            ${ref('companyNameElement')}
            ?disabled="${(x) => !x.isEditable}"
          >
            Company Name
          </zero-text-field>
          <div class="form-control">
            <label>Company Role</label>
            <zero-select
              class="input-control"
              :value=${sync((x) => x.companyRoleValue)}
              ${ref('companyRoleElement')}
              ?disabled="${(x) => !x.isEditable}"
            >
              <zero-option value="">Select Company role</zero-option>
              ${repeat(
                (x) => x.companyRoleList,
                html`
                  <zero-option value="${(x) => x.ENUM_VALUE}">
                    ${(x) => x.READABLE_VALUE}
                  </zero-option>
                `,
              )}
            </zero-select>
          </div>
          <div class="form-control date">
            <label>Physical Platform Access Agreement Date</label>
            <zero-text-field
              ${ref('nonSefParticipantAgreementElement')}
              class="input-control"
              type="date"
              onkeydown="return false"
              placeholder="Enter..."
              :value=${sync((x) => x.nonSefParticipantAgreementValue)}
              @input=${(x, c) => x.validateInputForDateElements(c.event)}
              ?disabled="${(x) => !x.isEditable}"
            ></zero-text-field>
          </div>
          <zero-text-field
            :value=${sync((x) => x.companyLEIValue)}
            placeholder="Enter..."
            ${ref('companyLEIElement')}
            ?disabled="${(x) => !x.isEditable}"
          >
            Company LEI
          </zero-text-field>
          <div class="form-control">
            <label>Regulatory Status</label>
            <zero-select
              class="input-control"
              :value=${sync((x) => x.regulatoryStatusValue)}
              ${ref('regulatoryStatusElement')}
              ?disabled="${(x) => !x.isEditable}"
            >
              <zero-option value="">Select Regulatory Status</zero-option>
              ${repeat(
                (x) => x.regulatoryStatusList,
                html`
                  <zero-option value="${(x) => x.ENUM_VALUE}">
                    ${(x) => x.READABLE_VALUE}
                  </zero-option>
                `,
              )}
            </zero-select>
            <zero-text-field
              :value=${sync((x) => x.manualRegulatoryStatusValue)}
              placeholder="Enter..."
              style="margin-top: 8px; display: ${(x) =>
                x.regulatoryStatusValue === 'OTHER' ? '' : 'none'}"
              ${ref('manualRegulatoryStatusElement')}
              ?disabled="${(x) => !x.isEditable}"
            ></zero-text-field>
          </div>
          <zero-number-field
            hide-step
            :value=${sync((x) => x.companyPhoneNumberValue)}
            placeholder="Enter..."
            ${ref('companyPhoneNumberElement')}
            ?disabled="${(x) => !x.isEditable}"
          >
            Company Phone Number
          </zero-number-field>
        </div>
        <div class="form-column">
          <zero-text-field
            :value=${sync((x) => x.companyAddressStreetValue)}
            placeholder="Enter..."
            ${ref('companyAddressStreetElement')}
            ?disabled="${(x) => !x.isEditable}"
          >
            Company Address Street
          </zero-text-field>
          <zero-text-field
            :value=${sync((x) => x.companyAddressCityValue)}
            placeholder="Enter..."
            ${ref('companyAddressCityElement')}
            ?disabled="${(x) => !x.isEditable}"
          >
            Company Address City
          </zero-text-field>
          <div class="form-control">
            <label>Company Address State</label>
            <zero-select
              class="input-control"
              :value=${sync((x) => x.companyAddressStateValue)}
              ${ref('companyAddressStateElement')}
              ?disabled="${(x) => !x.isEditable}"
            >
              <zero-option value="">Select Company Address State</zero-option>
              ${repeat(
                (x) => x.companyAddressStateList,
                html`
                  <zero-option value="${(x) => x.STATE_NAME}">${(x) => x.STATE_NAME}</zero-option>
                `,
              )}
            </zero-select>
          </div>
          <zero-text-field
            ${ref('companyAddressZipCodeElement')}
            hide-step
            placeholder="Enter..."
            :value=${sync((x) => x.companyAddressZipCodeValue)}
            ?disabled="${(x) => !x.isEditable}"
          >
            Company Address Zip Code
          </zero-text-field>
          <div class="form-control">
            <label>Company Country</label>
            <zero-select
              class="input-control"
              :value=${sync((x) => x.companyCountryValue)}
              ${ref('companyCountryElement')}
              disabled="true"
            >
              <zero-option value="">Select Company Country</zero-option>
              ${repeat(
                (x) => x.companyCountryList,
                html`
                  <zero-option value="${(x) => x.COUNTRY_NAME}">
                    ${(x) => x.COUNTRY_NAME}
                  </zero-option>
                `,
              )}
            </zero-select>
          </div>
        </div>

        <div class="form-column">
          <span class="form-session">Special Purpose Vehicle</span>
          <div class="form-session-elements-horizontal">
            <zero-checkbox
              @click=${(x) => (x.isSpvValue = !x.isSpvValue)}
              ?current-checked=${(x) => x?.isSpvValue}
              ?disabled="${(x) =>
                x?.companyRoleValue !== COMPANY_ROLE.SELLER_ONLY || !x.isEditable}"
            ></zero-checkbox>
            <zero-select
              class="input-control"
              :value=${sync((x) => x.spvParentValue)}
              ${ref('spvParentCompanyElement')}
              ?disabled="${(x) => !x?.isSpvValue}"
            >
              <zero-option value="">Enter Parent Legal Entity/Corporate Parent</zero-option>
              ${repeat(
                (x) => x.availableParentCompaniesList,
                html`
                  <zero-option value="${(x) => x.CASCADE_COMPANY_ID}">${(x) => x.NAME}</zero-option>
                `,
              )}
            </zero-select>
          </div>
          <span class="form-session">Method of Execution</span>
          <div class="form-session-elements">
            <zero-checkbox
              @click=${(x) => (x.rfqNegotiationValue = !x.rfqNegotiationValue)}
              ?current-checked=${(x) => x?.rfqNegotiationValue}
              ?disabled="${(x) => !x.isEditable}"
            >
              RFQ Negotiation
            </zero-checkbox>
            <zero-checkbox
              @click="${(x) => (x.autoExecutionValue = !x.autoExecutionValue)}"
              ?current-checked=${(x) => x?.autoExecutionValue}
              ?disabled="${(x) => !x.isEditable}"
            >
              Auto-Execution
            </zero-checkbox>
          </div>
          <span class="form-session">Regulatory Information</span>
          <div class="form-session-elements">
            <zero-checkbox
              @click="${(x) => (x.federalEntityIndicatorValue = !x.federalEntityIndicatorValue)}"
              ?current-checked=${(x) => x?.federalEntityIndicatorValue}
              ?disabled="${(x) => !x.isEditable}"
            >
              Federal Entity Indicator
            </zero-checkbox>
            <zero-checkbox
              @click="${(x) =>
                (x.financialEntityIndicatorValue = !x.financialEntityIndicatorValue)}"
              ?current-checked=${(x) => x?.financialEntityIndicatorValue}
              ?disabled="${(x) => !x.isEditable}"
            >
              Financial Entity Indicator
            </zero-checkbox>
          </div>
          <span class="form-session">Credit Profile</span>
          <div class="form-session-elements">
            <zero-checkbox
              @click="${(x, c) => x.changedCreditProfile(CREDIT_PROFILE.INVESTMENT_GRADE)}"
              ?current-checked=${(x) => x.companyCreditProfile == CREDIT_PROFILE.INVESTMENT_GRADE}
              ?disabled="${(x) => !x.isEditable}"
            >Investment Grade</zero-checkbox>
            <zero-checkbox
              @click="${(x, c) => x.changedCreditProfile(CREDIT_PROFILE.NON_INVESTMENT_GRADE)}"
              ?current-checked=${(x) => x.companyCreditProfile == CREDIT_PROFILE.NON_INVESTMENT_GRADE}
              ?disabled="${(x) => !x.isEditable}"
            >Non-Investment Grade</zero-checkbox>
          </div>
        </div>
      </div>
      ${when(
        (x) => x.isEditable,
        html`
          <zero-button
            appearance="primary"
            @click=${(x) => x.update()}
            class="confirm-button"
            ?disabled=${(x) => x.isFormValid || !x.isEditable}
          >
            ${(x) => (x.isEdit ? 'Edit Company Details' : 'Create New Company')}
          </zero-button>
          <zero-button
            appearance="secondary"
            @click=${(x) => x.clear()}
            class="confirm-button"
            ?disabled=${(x) => !x.isEditable}
          >
            Clear
          </zero-button>
        `,
      )}
      ${when(
        (x) => false,
        html<EditCompany>`
          <zero-button
            appearance="primary"
            @click=${(x) => x.viewSefAgreement()}
            class="confirm-button"
            ?disabled="${sync((x) => !x.selectedCompanyForSefAgreement)}"
          >
            View SEF Agreement for ${sync((x) => x.selectedCompanyForSefAgreement?.NAME)}
          </zero-button>
          <div class="sef-group">
            <label>
              View SEF Agreement
              <span style="color: red;">(${(x) => x.companies.length})</span>
            </label>
            <zero-select
              ${ref('sefCompanyElement')}
              class="sef-button"
              @change="${(x) => x.sefCompanySelected()}"
            >
              ${repeat(
                (x) => x.companies,
                html`
                  <zero-option value=${(x) => JSON.stringify(x)}>${(x) => x.NAME}</zero-option>
                `,
              )}
            </zero-select>
          </div>
        `,
      )}
    </zero-tab-panel>

    <zero-tab-panel
      style="display: ${(x) =>
        (x.isEdit && x.isAdmin)? '' : 'none'}"
    >
      <assigned-users
        ${ref('assignedUsers')}
        @companyHasUser="${(x, c) => (x.companyHasUser = eventDetail(c))}"
      ></assigned-users>
    </zero-tab-panel>
  </zero-tabs>
`;
